import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, Sun, Moon } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { useTheme } from "../context/ThemeContext";

const Navbar = () => {
  const { t, lang, setLang, languages } = useI18n();
  const { theme, toggle } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { label: t("nav.about"), id: "about" },
    { label: t("nav.services"), id: "services" },
    { label: t("nav.projects"), id: "projects" },
    { label: t("nav.whyUs"), id: "why" },
    { label: t("nav.contact"), id: "contact" }
  ];

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-mayak-bg/80 backdrop-blur-xl border-b border-mayak-border"
            : "bg-transparent"
        }`}
        data-testid="main-navbar"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
          <button
            onClick={() => scrollTo("hero")}
            className="flex items-center gap-2.5 group"
            data-testid="logo-home-link"
          >
            <LighthouseMark />
            <span className="font-black text-xl tracking-tight text-mayak-text">
              MAYAK
              <span className="text-mayak-accent">.</span>
            </span>
          </button>

          <nav className="hidden lg:flex items-center gap-10">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => scrollTo(l.id)}
                className="text-[13px] uppercase tracking-[0.2em] text-mayak-dim hover:text-mayak-text mayak-link-underline transition-colors"
                data-testid={`nav-link-${l.id}`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={toggle}
              className="theme-toggle"
              data-testid="theme-toggle-button"
              aria-label={t("theme.toggle")}
              title={theme === "dark" ? t("theme.light") : t("theme.dark")}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={theme}
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex"
                >
                  {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
                </motion.span>
              </AnimatePresence>
            </button>

            <div className="relative">
              <button
                onClick={() => setLangOpen((o) => !o)}
                className="hidden sm:flex items-center gap-2 px-3 py-2 border border-mayak-border-strong text-mayak-dim hover:text-mayak-text hover:border-mayak-accent transition-all text-xs uppercase tracking-[0.2em]"
                data-testid="lang-toggle-button"
              >
                <Globe size={14} />
                {lang.toUpperCase()}
              </button>
              <AnimatePresence>
                {langOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    className="absolute right-0 mt-2 min-w-[120px] bg-mayak-surface border border-mayak-border shadow-xl"
                    data-testid="lang-dropdown"
                  >
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => {
                          setLang(l.code);
                          setLangOpen(false);
                        }}
                        className={`w-full text-left px-4 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-mayak-bg-alt transition-colors ${
                          lang === l.code ? "text-mayak-accent" : "text-mayak-dim"
                        }`}
                        data-testid={`lang-option-${l.code}`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              onClick={() => scrollTo("contact")}
              className="hidden md:inline-flex magnetic-btn !py-3 !px-5 !text-xs"
              data-testid="nav-cta-button"
            >
              {t("nav.cta")}
            </button>

            <button
              className="lg:hidden text-mayak-text"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-menu-open"
              aria-label="Open menu"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-mayak-bg lg:hidden"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex justify-between items-center h-20 px-6">
              <span className="font-black text-xl tracking-tight text-mayak-text">
                MAYAK<span className="text-mayak-accent">.</span>
              </span>
              <button
                onClick={() => setMobileOpen(false)}
                className="text-mayak-text"
                data-testid="mobile-menu-close"
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>
            <div className="flex flex-col px-6 gap-6 pt-12">
              {links.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                  onClick={() => scrollTo(l.id)}
                  className="text-3xl font-black text-mayak-text text-left"
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {l.label}
                </motion.button>
              ))}
              <div className="mt-8 flex gap-3">
                {languages.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => setLang(l.code)}
                    className={`px-4 py-2 border text-xs uppercase tracking-[0.2em] ${
                      lang === l.code
                        ? "border-mayak-accent text-mayak-accent"
                        : "border-mayak-border-strong text-mayak-dim"
                    }`}
                    data-testid={`mobile-lang-${l.code}`}
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const LighthouseMark = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" className="text-mayak-accent">
    <path d="M14 3 L11 8 H17 L14 3 Z" fill="currentColor" />
    <rect x="11.5" y="8" width="5" height="2" fill="currentColor" />
    <path d="M11 10 L17 10 L18 24 L10 24 Z" stroke="currentColor" strokeWidth="1.4" fill="none" />
    <line x1="11" y1="14" x2="17" y2="14" stroke="currentColor" strokeWidth="1" />
    <line x1="10" y1="24" x2="18" y2="24" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="14" cy="6" r="1" fill="currentColor" />
  </svg>
);

export default Navbar;
